import { useMemo, useState } from 'react';
import { ChevronDown } from 'lucide-react';
import { cn } from '@/lib/utils';
import { parseChangelogBlockHtml } from '@/components/painel/changelog/parse-changelog-block-html';
import { getChangelogSectionVisual } from '@/lib/changelog/changelog-section-visuals';
import type { SectionType } from '@/lib/changelog/changelog-types';
import PowerMentionText from '@/components/help-center/powers/PowerMentionText';

export type ChangelogEditorPreviewBlock = {
  title: string;
  type: SectionType;
  accent: string;
  html: string;
};

/** Menções legíveis no fundo escuro da prévia ao vivo. */
const previewMentionClass =
  'text-sky-300 decoration-sky-400/50 hover:text-sky-200 hover:underline font-semibold';

function PreviewMention({ text, className }: { text: string; className?: string }) {
  return (
    <PowerMentionText
      text={text}
      as="span"
      className={className}
      mentionClassName={previewMentionClass}
    />
  );
}

export function ChangelogEditorBlockPreview({ block }: { block: ChangelogEditorPreviewBlock }) {
  const items = useMemo(() => parseChangelogBlockHtml(block.html), [block.html]);
  const visual = getChangelogSectionVisual(block.type);
  const Icon = visual.icon;

  const displayItems = useMemo(() => {
    if (items.length > 0) return items;
    return [
      {
        title: 'Pré-visualização',
        details: ['O que você escrever no editor aparece aqui no formato do changelog.'],
      },
    ];
  }, [items]);

  const [open, setOpen] = useState(true);
  const entryCount = displayItems.length;
  const accent = block.accent || visual.accent;

  return (
    <div
      className="overflow-hidden rounded-2xl border border-white/10 bg-[#05051f]/50 shadow-[0_12px_40px_-24px_rgba(0,0,0,0.5)]"
      style={{ borderColor: `${accent}66`, boxShadow: `0 16px 48px -28px ${accent}44` }}
    >
      <button
        type="button"
        className="flex w-full items-center justify-between gap-2 px-4 py-3 text-left transition-colors hover:bg-white/[0.04]"
        onClick={() => setOpen((v) => !v)}
        aria-expanded={open}
      >
        <div className="flex min-w-0 flex-1 items-center gap-3">
          <div
            className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg"
            style={{ backgroundColor: `${accent}28`, boxShadow: `inset 0 0 0 1px ${accent}40` }}
          >
            <Icon className="h-4 w-4" style={{ color: accent }} aria-hidden />
          </div>
          <span className="min-w-0 truncate font-poppins text-sm font-bold text-sky-100">{block.title}</span>
          <span
            className="shrink-0 rounded-full px-2 py-0.5 font-poppins text-xs font-semibold tabular-nums text-sky-100"
            style={{ backgroundColor: `${accent}30` }}
          >
            {entryCount}
          </span>
        </div>
        <ChevronDown
          className={cn('h-4 w-4 shrink-0 text-blue-100/50 transition-transform duration-300', open && 'rotate-180')}
        />
      </button>

      <div
        className={cn(
          'grid transition-[grid-template-rows] duration-300 ease-in-out',
          open ? 'grid-rows-[1fr]' : 'grid-rows-[0fr]'
        )}
      >
        <div className="overflow-hidden">
          <ul className="space-y-4 border-t border-white/[0.06] bg-[#05051f]/40 px-4 py-3">
            {displayItems.map((item, i) => (
              <li key={i} className="flex gap-3">
                <span
                  className="mt-1.5 h-1.5 w-1.5 shrink-0 rounded-full"
                  style={{ backgroundColor: accent }}
                />
                <div className="min-w-0">
                  {item.inlineDescription ? (
                    <p className="font-inter text-sm leading-relaxed text-blue-100/85">
                      <PreviewMention text={item.title} className="font-semibold text-sky-100" />
                      <span className="text-slate-500"> — </span>
                      <PreviewMention text={item.inlineDescription} />
                    </p>
                  ) : (
                    <>
                      <p className="font-inter text-sm font-semibold text-white">
                        <PreviewMention text={item.title} />
                      </p>
                      {item.details.length > 0 ? (
                        <ul className="mt-1.5 space-y-1">
                          {item.details.map((d, j) => (
                            <li key={j} className="flex gap-2 font-inter text-sm text-blue-100/75">
                              <span className="mt-1.5 h-1 w-1 shrink-0 rounded-full bg-white/25" />
                              <PreviewMention text={d} />
                            </li>
                          ))}
                        </ul>
                      ) : null}
                    </>
                  )}
                </div>
              </li>
            ))}
          </ul>
        </div>
      </div>
    </div>
  );
}
